import moment from 'moment';
import { GPS_HISTORY_ISIN } from './templateConstants';

/**
 * Static GPS / Global Premium Select (P) fund data from PVM Stammdaten CSV
 * "[extern] PARTNERS VermögensManagement AG_ Stammdaten-Übersicht - Fondsdaten.csv"
 */
export const GPS_STAMMDATEN_DEFAULTS = {
  isin: GPS_HISTORY_ISIN,
  wkn: 'A2DKKP',
  fondsart: 'Multi-Asset Fonds',
  fondsdomizil: 'Luxemburg',
  anlageschwerpunkt: 'Global',
  srri: '3 von 7',
  auflagedatum: '2017-07-14',
  anlageberater: 'PARTNERS VermögensManagement AG',
  verwaltungsgesellschaft: 'Axxion S.A.',
  verwahrstelle: 'Hauck Aufhäuser Lampe Privatbank AG, Niederlassung Luxemburg',
  geschäftsjahr: '31. März',
  vertriebszulassung: 'Deutschland, Luxemburg, Österreich, Schweiz',
  kontakt:
    'PARTNERS VermögensManagement AG\nSüdliche Auffahrtsallee 37\n80639 München\nTel. +49(0)89 24 21 39 0\ninfo@pvm-ag.de',
} as const;

export const GPS_PREISE_DEFAULTS = {
  wahrung: 'EUR',
  ertragsart: 'Ausschüttend',
} as const;

export const GPS_KONDITIONEN_DEFAULTS = {
  verwaltungsgebühr: 'bis zu 1,54 % p.a.',
  performance_fee:
    'Die Gebühr wird anhand des Wertzuwachses in Kombination mit einer High Water Mark berechnet und zum Ende der Abrechnungsperiode ausbezahlt.',
  ausgabeaufschlag: 'bis zu 5,00 %',
} as const;

/** Frankfurter / SHV clone values left over when the PVM document was duplicated. */
const SHV_CLONE_FINGERPRINTS = {
  isin: ['LU2439874319'],
  wkn: ['FRA3TF'],
  bloomberg: ['FRUMVEI', 'FRUMVEI '],
  anlageberater: [/shareholder\s*value/i],
  nachhaltigkeit: [/artikel\s*8/i, /offenlegungsverordnung/i],
} as const;

function isBlank(value: unknown): boolean {
  return value === undefined || value === null || String(value).trim() === '';
}

/** Correct casing: VermögensManagement (capital M). */
function normalizePvmCompanyName(value: unknown): unknown {
  if (typeof value !== 'string' || !value.includes('Vermögensmanagement')) return value;
  return value.replace(/Vermögensmanagement/g, 'VermögensManagement');
}

function matchesFingerprint(field: keyof typeof SHV_CLONE_FINGERPRINTS, value: unknown): boolean {
  if (isBlank(value)) return false;
  const raw = String(value).trim();
  const patterns = SHV_CLONE_FINGERPRINTS[field];
  return patterns.some((p) => (typeof p === 'string' ? raw === p : p.test(raw)));
}

function shouldReplaceStammdatenField(
  field: keyof typeof GPS_STAMMDATEN_DEFAULTS,
  current: unknown,
): boolean {
  if (isBlank(current)) return true;
  if (field in SHV_CLONE_FINGERPRINTS) {
    return matchesFingerprint(field as keyof typeof SHV_CLONE_FINGERPRINTS, current);
  }
  if (field === 'fondsart' && String(current).trim() === 'UCITS') return true;
  if (field === 'auflagedatum') {
    const m = moment(String(current).slice(0, 10), ['YYYY-MM-DD', 'DD.MM.YYYY'], true);
    if (m.isValid() && m.year() >= 2020) return true;
  }
  if (field === 'anlageberater' && /shareholder\s*value/i.test(String(current))) return true;
  if (field === 'verwahrstelle' && /hauck aufhäuser lampe privatbank ag$/i.test(String(current).trim())) {
    return true;
  }
  return false;
}

export function applyGpsStammdatenDefaults(stammdaten: Record<string, unknown> = {}): Record<string, unknown> {
  const next = { ...stammdaten };
  for (const [field, defaultValue] of Object.entries(GPS_STAMMDATEN_DEFAULTS)) {
    if (shouldReplaceStammdatenField(field as keyof typeof GPS_STAMMDATEN_DEFAULTS, next[field])) {
      next[field] = defaultValue;
    }
  }
  if (matchesFingerprint('bloomberg', next.bloomberg)) {
    delete next.bloomberg;
  }
  if (matchesFingerprint('nachhaltigkeit', next.nachhaltigkeit)) {
    delete next.nachhaltigkeit;
  }
  // Upgrade stored company name casing (m → M) without wiping other edits
  for (const field of ['anlageberater', 'kontakt'] as const) {
    next[field] = normalizePvmCompanyName(next[field]);
  }
  return next;
}

export function applyGpsPreiseDefaults(preise: Record<string, unknown> = {}): Record<string, unknown> {
  const next = { ...preise };
  for (const [field, defaultValue] of Object.entries(GPS_PREISE_DEFAULTS)) {
    if (isBlank(next[field])) {
      next[field] = defaultValue;
    }
  }
  return next;
}

export function applyGpsKonditionenDefaults(konditionen: Record<string, unknown> = {}): Record<string, unknown> {
  const next = { ...konditionen };
  for (const [field, defaultValue] of Object.entries(GPS_KONDITIONEN_DEFAULTS)) {
    const current = next[field];
    const isShvClone =
      (field === 'verwaltungsgebühr' && /1,83\s*%/.test(String(current || ''))) ||
      (field === 'performance_fee' && /15,00\s*%/.test(String(current || ''))) ||
      (field === 'ausgabeaufschlag' && /bis zu 5,00\s*%/.test(String(current || '')) && /15,00\s*%/.test(String(next.performance_fee || '')));
    if (isBlank(current) || isShvClone) {
      next[field] = defaultValue;
    }
  }
  return next;
}
